import React, { useState } from 'react'

interface LegalComplianceNoticeProps {
  context: 'messaging' | 'vendor_contact' | 'registration' | 'general'
  onDismiss?: () => void
  persistent?: boolean
}

export default function LegalComplianceNotice({ 
  context, 
  onDismiss, 
  persistent = false 
}: LegalComplianceNoticeProps) {
  const [isDismissed, setIsDismissed] = useState(false)

  const handleDismiss = () => {
    setIsDismissed(true)
    onDismiss?.()
  }

  if (isDismissed && !persistent) return null

  const getNoticeContent = () => {
    switch (context) {
      case 'messaging':
        return {
          icon: '💬',
          title: 'Messaging Compliance',
          content: (
            <div className="compliance-content">
              <p>
                <strong>Important:</strong> All communications must comply with federal, state, 
                and local laws. Kratom legality varies by jurisdiction.
              </p>
              <ul>
                <li>No medical claims about kratom products</li>
                <li>Business communications must follow commercial regulations</li>
                <li>Age verification required (21+ only)</li>
                <li>Respect vendor communication preferences</li>
              </ul>
            </div>
          )
        }
      
      case 'vendor_contact':
        return {
          icon: '🏪',
          title: 'Vendor Contact Guidelines',
          content: (
            <div className="compliance-content">
              <p>
                <strong>Vendor Protection:</strong> Please be respectful and professional 
                when contacting vendors.
              </p>
              <ul>
                <li>Rate limits apply: 10 messages per 5 minutes, 60 per hour</li>
                <li>New users may require approval before messaging vendors</li>
                <li>Spam or harassment will result in account restrictions</li>
                <li>Focus on legitimate business inquiries</li>
              </ul>
            </div>
          )
        }
      
      case 'registration':
        return {
          icon: '✍️',
          title: 'Registration Requirements',
          content: (
            <div className="compliance-content">
              <p>
                <strong>Legal Requirements:</strong> By creating an account, you confirm:
              </p>
              <ul>
                <li>You are at least 21 years of age</li>
                <li>Kratom is legal in your jurisdiction</li>
                <li>You agree to our Terms of Service and Privacy Policy</li>
                <li>You will comply with all applicable laws</li>
                <li>You understand this platform is for educational purposes</li>
              </ul>
            </div>
          )
        }
      
      default:
        return {
          icon: '⚖️',
          title: 'Legal Compliance',
          content: (
            <div className="compliance-content">
              <p>
                <strong>Platform Notice:</strong> This platform is for educational and 
                informational purposes regarding kratom.
              </p>
              <ul>
                <li>Kratom legality varies by location</li>
                <li>No medical advice is provided</li>
                <li>Users must comply with local laws</li>
                <li>Age verification required (21+)</li>
              </ul>
            </div>
          )
        }
    }
  }

  const notice = getNoticeContent()

  return (
    <div className={`legal-compliance-notice ${context}`}>
      <div className="notice-container">
        <div className="notice-header">
          <span className="notice-icon">{notice.icon}</span>
          <h4 className="notice-title">{notice.title}</h4>
          {!persistent && (
            <button 
              className="dismiss-btn"
              onClick={handleDismiss}
              title="Dismiss notice"
            >
              ×
            </button>
          )}
        </div>
        
        <div className="notice-body">
          {notice.content}
        </div>
        
        <div className="notice-footer">
          <div className="footer-links">
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
            <span className="separator">•</span>
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            <span className="separator">•</span>
            <a href="/legal-disclaimer" target="_blank" rel="noopener noreferrer">
              Legal Disclaimer
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

// Hook for managing legal notice dismissal state
export function useLegalNotice(noticeKey: string) {
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem(`legal_notice_dismissed_${noticeKey}`) === 'true'
  })

  const dismissNotice = () => {
    localStorage.setItem(`legal_notice_dismissed_${noticeKey}`, 'true')
    setIsDismissed(true)
  }

  const resetNotice = () => {
    localStorage.removeItem(`legal_notice_dismissed_${noticeKey}`)
    setIsDismissed(false)
  }

  return { isDismissed, dismissNotice, resetNotice }
}

// Component for displaying persistent legal warnings in sensitive areas
export function PersistentLegalWarning({ 
  type = 'general' 
}: { 
  type?: 'messaging' | 'vendor' | 'privacy' | 'general' 
}) {
  const getWarningContent = () => {
    switch (type) {
      case 'messaging':
        return {
          icon: '⚠️',
          text: 'Messages monitored for compliance. Private mode available for sensitive discussions.',
          severity: 'warning'
        }
      case 'vendor':
        return {
          icon: '🏪',
          text: 'Vendor interactions subject to rate limits and approval requirements.',
          severity: 'info'
        }
      case 'privacy':
        return {
          icon: '🔒',
          text: 'Private mode uses end-to-end encryption. Users remain responsible for legal compliance.',
          severity: 'info'
        }
      default:
        return {
          icon: '⚖️',
          text: 'Educational platform only. Comply with local laws. No medical advice provided.',
          severity: 'warning'
        }
    }
  }

  const warning = getWarningContent()

  return (
    <div className={`persistent-legal-warning ${warning.severity}`}>
      <span className="warning-icon">{warning.icon}</span>
      <span className="warning-text">{warning.text}</span>
    </div>
  )
}